/**
 * Report form (information meeting).
 */
import * as React from 'react';
import { FileText, Star } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { Textarea } from '@/components/forms/Textarea';
import { Rating } from '@/components/forms/Rating';
import { getReport, updateReport } from '@/services/inperson/reports.service';
import { getSession } from '@/services/inperson/sessions.service';
import { getReportDisplayRating } from '@/utils/report-author-feedback-utils';
import type { ParticipantAttendance } from '@/types/attendance.types';
import type { UpdateReportDto } from '@/types/inperson/report.types';
import { REPORT_IN_PERSON_QUERY_KEYS as QUERY_KEYS } from '@/components/reports/report-configs';
import { AttendanceSection } from './AttendanceSection';
import {
  buildUpdateInformationMeetingReportDto,
  collectAttendanceErrors,
  initAttendanceMap,
  isAttendanceMapValid,
  setAttendanceEntry,
  isArchivedParticipant,
} from '@/components/reports/inperson/report-form-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link InformationMeetingForm}. */
export interface InformationMeetingFormProps {
  sessionId: string;
  reportId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Edit form for an in-person information-meeting report. */
export function InformationMeetingForm(props: Readonly<InformationMeetingFormProps>) {
  const { sessionId, reportId, onClose, onSuccess } = props;
  const queryClient = useQueryClient();

  const [freeSynthesis, setFreeSynthesis] = React.useState('');
  const [engagementLevel, setEngagementLevel] = React.useState<number | null>(null);
  const [attendanceData, setAttendanceData] = React.useState<Map<string, ParticipantAttendance>>(
    () => new Map(),
  );
  const [attendanceErrors, setAttendanceErrors] = React.useState<Map<string, string>>(
    () => new Map(),
  );

  const { data: report, isLoading } = useQuery({
    queryKey: QUERY_KEYS.report(sessionId, reportId),
    queryFn: () => getReport(sessionId, reportId),
  });

  const { data: session } = useQuery({
    queryKey: QUERY_KEYS.session(sessionId),
    queryFn: () => getSession(sessionId),
    enabled: !!sessionId,
  });

  React.useEffect(() => {
    if (!report) return;
    setFreeSynthesis(report.comment ?? '');
    setEngagementLevel(getReportDisplayRating(report));
  }, [report]);

  const editableAttendanceParticipants = React.useMemo(
    () => (session?.participants ?? []).filter((p) => !isArchivedParticipant(p)),
    [session?.participants],
  );

  React.useEffect(() => {
    if (!editableAttendanceParticipants.length) {
      setAttendanceData(new Map());
      setAttendanceErrors(new Map());
      return;
    }
    setAttendanceData(initAttendanceMap(editableAttendanceParticipants));
    setAttendanceErrors(new Map());
  }, [editableAttendanceParticipants]);

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateReportDto) => updateReport(sessionId, reportId, dto),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.list, refetchType: 'all' }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.sessionDetail(sessionId),
          refetchType: 'all',
        }),
      ]);
      toast.success('Compte rendu modifié avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const isAttendanceValid = React.useMemo(
    () => isAttendanceMapValid(attendanceData, editableAttendanceParticipants.length),
    [attendanceData, editableAttendanceParticipants],
  );

  /** Validate, then PATCH the information-meeting report. */
  const handleSubmit = () => {
    if (!freeSynthesis.trim()) {
      toast.error(getErrorMessage('reportSynthesisRequired'));
      return;
    }
    if (engagementLevel === null) {
      toast.error(getErrorMessage('engagementLevelRequired'));
      return;
    }

    if (editableAttendanceParticipants.length > 0) {
      const errors = collectAttendanceErrors(attendanceData);
      if (errors.size > 0) {
        setAttendanceErrors(errors);
        toast.error(getErrorMessage('requiredAttendanceReasons'));
        return;
      }
    }

    updateMutation.mutate(
      buildUpdateInformationMeetingReportDto({
        freeSynthesis,
        engagementLevel,
        attendanceParticipants: editableAttendanceParticipants,
        attendanceData,
      }),
    );
  };

  const isValid = freeSynthesis.trim().length > 0 && engagementLevel !== null && isAttendanceValid;

  return (
    <CollapseReplaceForm
      formType="edit"
      title="Modifier la synthèse de réunion d'information"
      subtitle="Modifiez les informations de la synthèse de réunion d'information"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={isValid}
      isLoading={isLoading}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Synthèse de la réunion
        </h4>
        <Textarea
          label="Synthèse libre"
          required
          placeholder="Décrivez le déroulement et les points abordés lors de la réunion d'information…"
          value={freeSynthesis}
          onChange={(e) => setFreeSynthesis(e.target.value)}
          rows={6}
          autoResize
          maxLength={3000}
          showCharCount
        />
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <Star className="w-4 h-4" />
          Engagement des participants
        </h4>
        <Rating
          label="Niveau d'engagement"
          required
          value={engagementLevel}
          onChange={(v) => setEngagementLevel(v)}
          helperText="Évaluez l'engagement général des participants /5"
        />
      </div>

      <AttendanceSection
        participants={editableAttendanceParticipants}
        attendanceData={attendanceData}
        errors={attendanceErrors}
        onChange={(id, next) => setAttendanceData((prev) => setAttendanceEntry(prev, id, next))}
      />
    </CollapseReplaceForm>
  );
}
